// TypeToConfirm: the question in front of something that cannot be undone. The person types the
// thing's name back, and only then does the one solid red key arm. docs/operations/design-standard.md,
// "Controls".

import React, { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import Dialog from './Dialog';
import FadeIn from './FadeIn';
import { Field } from './Field';
import { IconCheck } from './icons';
import Tooltip from './Tooltip';

let seq = 0;

export interface TypeToConfirmProps {
  open: boolean;
  onCancel: () => void;
  /**
   * Runs when the armed key is pressed. A promise holds the dialog busy until it settles; a
   * rejection shows its message and keeps the dialog open. Closing on success is the caller's.
   */
  onConfirm: () => void | Promise<unknown>;
  /** The question, naming the action: "Delete this vendor". */
  title: string;
  /** What happens, and what cannot be undone. */
  children?: React.ReactNode;
  /** The words to type back: the record's name, serial or SKU. */
  phrase: string;
  /** What the phrase is, for the prompt: "the drone's serial". */
  phraseNoun?: string;
  confirmLabel: string;
  /** The key's label while the work runs ("Deleting"). */
  busyLabel?: string;
  cancelLabel?: string;
  /** Letter case must match too. Off by default, so a serial typed in lower case still counts. */
  matchCase?: boolean;
  returnTo?: React.RefObject<HTMLElement | null>;
  light?: boolean;
}

const squash = (s: string) => s.trim().replace(/\s+/g, ' ');

const messageOf = (err: unknown): string =>
  err instanceof Error && err.message ? err.message : 'It did not go through. Try again.';

export const TypeToConfirm: React.FC<TypeToConfirmProps> = ({
  open, onCancel, onConfirm, title, children, phrase, phraseNoun, confirmLabel, busyLabel,
  cancelLabel = 'Cancel', matchCase, returnTo, light,
}) => {
  const [ids] = useState(() => { const n = ++seq; return { field: `ui-ttc-${n}`, status: `ui-ttc-${n}-status` }; });
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  useEffect(() => {
    if (!open) return;
    setTyped('');
    setProblem(null);
    setBusy(false);
  }, [open]);

  const same = (a: string, b: string) => (matchCase ? squash(a) === squash(b) : squash(a).toLowerCase() === squash(b).toLowerCase());
  const armed = squash(typed) !== '' && same(typed, phrase);

  const submit = async () => {
    if (busy) return;
    if (!armed) { setProblem(`Type ${phrase} exactly to go on.`); return; }
    setProblem(null);
    let result: void | Promise<unknown>;
    try { result = onConfirm(); } catch (err) { setProblem(messageOf(err)); return; }
    if (!result || typeof (result as Promise<unknown>).then !== 'function') return;
    setBusy(true);
    try {
      await result;
    } catch (err) {
      if (alive.current) setProblem(messageOf(err));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const muted = light ? 'text-gray-700' : 'text-on-surface-variant';
  const strong = light ? 'text-gray-900' : 'text-on-surface';
  const good = light ? 'text-green-700' : 'text-green-300';

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      tone="danger"
      alert
      size="md"
      hideClose
      busy={busy}
      returnTo={returnTo}
      light={light}
      onSubmit={submit}
      problem={problem}
      footer={(
        <>
          <Button variant="tonal" disabled={busy} onClick={onCancel}>{cancelLabel}</Button>
          <Tooltip label="This cannot be undone" note={armed ? undefined : `Type ${phrase} first`}>
            <Button type="submit" variant="danger-solid" disabled={!armed || busy}>
              {busy && busyLabel ? busyLabel : confirmLabel}
            </Button>
          </Tooltip>
        </>
      )}
    >
      {children ? <div className={`mb-4 text-sm leading-5 ${muted}`}>{children}</div> : null}
      <label htmlFor={ids.field} className={`mb-1.5 block text-[13px] font-medium leading-[18px] ${muted}`}>
        Type{' '}
        {phraseNoun ? <>{phraseNoun}, </> : null}
        <span className={`font-semibold [overflow-wrap:anywhere] ${strong}`}>{phrase}</span>
        {phraseNoun ? ',' : ''} to confirm
      </label>
      <Field
        id={ids.field}
        autoFocus
        value={typed}
        readOnly={busy}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        aria-describedby={ids.status}
        onChange={(e) => { setTyped(e.target.value); setProblem(null); }}
      />
      <div id={ids.status} role="status" className="mt-1.5 flex h-5 items-center text-[13px] leading-[18px]">
        {armed ? (
          <FadeIn key="armed" className={`flex items-center gap-1.5 ${good}`}>
            <IconCheck draw className="h-3.5 w-3.5 flex-none" />
            Matches
          </FadeIn>
        ) : (
          <FadeIn key="hint" className={muted}>
            {matchCase ? 'Letter case counts.' : 'Letter case does not matter.'}
          </FadeIn>
        )}
      </div>
    </Dialog>
  );
};

export default TypeToConfirm;
